import React, { useMemo } from 'react';
import { Eye, CalendarOff } from 'lucide-react';
import { WEEKDAYS, computeWeekdayWindows, type OperatingHours } from '../hooks/useServices';
import type { ResourceForm } from './ServiceResourcesEditor';
import type { ClosureForm } from './ServiceClosuresEditor';
import { describeDayRange } from '../utils/pricingSeasons';

/**
 * SAM-546 (N11) — "previsualiza el horario efectivo y el aforo" acceptance
 * criterion. Read-only, computed entirely client-side from the form state
 * already on screen (operating hours, closures, resources, capacity fields)
 * — nothing here is sent anywhere, it mirrors what `engine_sync.py` will
 * compute from the SAME fields once saved (`weekday_windows` for the
 * schedule split, the resource/default capacity fallback chain for aforo),
 * so an admin can catch a midnight-crossing window or a capacity mismatch
 * before hitting Save instead of after a sync failure.
 */

function formatClock(value: string): string {
  return value.slice(0, 5);
}

interface ServiceEffectivePreviewProps {
  operatingHours: OperatingHours;
  closures: ClosureForm[];
  resources: ResourceForm[];
  bookingType: 'fixed' | 'day' | 'flexible';
  maxCapacity: string;
  maxPerSlot: string;
  dailyCapacity: string;
  /** SAM-198 editor v2: false when the weekly bars already show the schedule next to it. */
  showSchedule?: boolean;
}

export const ServiceEffectivePreview: React.FC<ServiceEffectivePreviewProps> = ({
  operatingHours,
  closures,
  resources,
  bookingType,
  maxCapacity,
  maxPerSlot,
  dailyCapacity,
  showSchedule = true,
}) => {
  const windowsByDay = useMemo(() => computeWeekdayWindows(operatingHours), [operatingHours]);

  const capacitySummary = useMemo(() => {
    if (resources.length === 0) {
      const capacity = maxCapacity ? Number(maxCapacity) : null;
      const perUnit = bookingType === 'day'
        ? (dailyCapacity ? Number(dailyCapacity) : 1)
        : (maxPerSlot ? Number(maxPerSlot) : 1);
      return {
        resourceCount: 1,
        totalCapacity: capacity,
        totalSimultaneous: perUnit,
      };
    }
    let totalCapacity = 0;
    let anyCapacity = false;
    let totalSimultaneous = 0;
    resources.forEach((r) => {
      const cap = r.capacity ? Number(r.capacity) : (maxCapacity ? Number(maxCapacity) : null);
      if (cap != null && !Number.isNaN(cap)) { totalCapacity += cap; anyCapacity = true; }
      const simul = r.max_simultaneous_bookings
        ? Number(r.max_simultaneous_bookings)
        : (bookingType === 'day' ? (dailyCapacity ? Number(dailyCapacity) : 1) : (maxPerSlot ? Number(maxPerSlot) : 1));
      totalSimultaneous += Number.isNaN(simul) ? 0 : simul;
    });
    return {
      resourceCount: resources.length,
      totalCapacity: anyCapacity ? totalCapacity : null,
      totalSimultaneous,
    };
  }, [resources, maxCapacity, maxPerSlot, dailyCapacity, bookingType]);

  const sortedClosures = useMemo(
    () => [...closures]
      .filter((c) => c.start_date && c.end_date)
      .sort((a, b) => a.start_date.localeCompare(b.start_date)),
    [closures]
  );

  return (
    <div className="text-sm">
      {showSchedule && (<>
      <div className="flex items-center gap-2 mb-2">
        <Eye size={13} style={{ color: 'var(--ink-500)' }} />
        <span className="text-xs font-medium" style={{ color: 'var(--ink-700)' }}>
          Effective weekly schedule
        </span>
      </div>
      <div className="rounded-lg overflow-hidden mb-3" style={{ border: '1px solid var(--line)' }}>
        {WEEKDAYS.map(({ key, label }, i) => {
          const dayWindows = windowsByDay[key] || [];
          return (
            <div
              key={key}
              className="flex items-center justify-between px-3 py-1.5 text-xs"
              style={{
                background: i % 2 === 0 ? 'var(--surface-2)' : 'transparent',
                borderTop: i === 0 ? undefined : '1px solid var(--line-subtle)',
              }}
            >
              <span style={{ color: 'var(--ink-700)', fontWeight: 600, width: '32px' }}>{label}</span>
              {dayWindows.length === 0 ? (
                <span style={{ color: 'var(--ink-400)' }} className="italic">Closed</span>
              ) : (
                <span style={{ color: 'var(--ink-500)' }}>
                  {dayWindows.map(([open, close]) => `${formatClock(open)}–${formatClock(close)}`).join(', ')}
                </span>
              )}
            </div>
          );
        })}
      </div>
      </>)}

      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium" style={{ color: 'var(--ink-700)' }}>Effective aforo (capacity)</span>
      </div>
      <div className="rounded-lg p-3 mb-3 text-xs" style={{ border: '1px solid var(--line)', background: 'var(--surface-2)' }}>
        <div className="flex justify-between py-0.5">
          <span style={{ color: 'var(--ink-500)' }}>Resources</span>
          <span style={{ color: 'var(--ink-900)' }}>
            {resources.length === 0 ? '1 pool (no named resources)' : capacitySummary.resourceCount}
          </span>
        </div>
        <div className="flex justify-between py-0.5">
          <span style={{ color: 'var(--ink-500)' }}>Total capacity (pax)</span>
          <span style={{ color: 'var(--ink-900)' }}>{capacitySummary.totalCapacity ?? '—'}</span>
        </div>
        <div className="flex justify-between py-0.5">
          <span style={{ color: 'var(--ink-500)' }}>
            {bookingType === 'day' ? 'Total daily capacity' : 'Total simultaneous bookings / slot'}
          </span>
          <span style={{ color: 'var(--ink-900)' }}>{capacitySummary.totalSimultaneous}</span>
        </div>
      </div>

      {sortedClosures.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2">
            <CalendarOff size={13} style={{ color: 'var(--ink-500)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--ink-700)' }}>Upcoming closures</span>
          </div>
          <ul className="text-xs space-y-1">
            {sortedClosures.map((c, i) => (
              <li key={i} className="flex items-center justify-between">
                <span style={{ color: 'var(--ink-700)' }}>
                  {describeDayRange(c.start_date, c.end_date)}
                </span>
                {c.reason && <span style={{ color: 'var(--ink-400)' }}>{c.reason}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
